/* iter-p3-v — Compact AI-share sparkline for the MyBillingPanel.
 *
 * Consumes GET /api/editing-record/user-trends?days=30 and renders a
 * 30-point line + area chart of AI-share % over time.  Zero-data days
 * are stitched at the last observed value (rather than dropped to 0)
 * so the line stays readable when writers skip a day.  Pure SVG so we
 * don't pull in a chart library for a 90px widget.
 *
 * iter-p3-w — Points are now tappable, opening a deep-dive dialog
 * that lists the projects that drove that day's AI-share.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, Loader2 } from 'lucide-react';
import AiShareTrendDeepDiveDialog from './AiShareTrendDeepDiveDialog';

const API = process.env.REACT_APP_BACKEND_URL;
const W = 300;
const H = 60;
const PAD = 4;

export default function AiShareTrendSparkline({ days = 30 }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDay, setOpenDay] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/editing-record/user-trends?days=${days}`,
              { withCredentials: true, timeout: 15000 })
      .then((r) => { if (!cancelled) setData(r.data || null); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[11.5px] text-muted-foreground" data-testid="ai-share-sparkline-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading AI-share trend…
      </div>
    );
  }
  const series = data?.series || [];
  const total  = series.reduce((n, s) => n + (s.total || 0), 0);
  if (!series.length || total === 0) {
    return (
      <div className="text-[11.5px] text-muted-foreground" data-testid="ai-share-sparkline-empty">
        No editing-record actions yet — write or generate anything and the trend appears here.
      </div>
    );
  }

  // Carry-forward for gaps so the line looks continuous.
  let last = 0;
  const pts = series.map((s, i) => {
    if (typeof s.ai_share_pct === 'number') last = s.ai_share_pct;
    return { i, day: s.day, share: last, total: s.total || 0 };
  });
  const n = pts.length;
  const x = (i) => PAD + (i * (W - PAD * 2)) / Math.max(1, n - 1);
  const y = (v) => H - PAD - (v / 100) * (H - PAD * 2);

  const linePath  = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(p.share).toFixed(1)}`).join(' ');
  const areaPath  = `${linePath} L${x(n - 1).toFixed(1)} ${H - PAD} L${PAD} ${H - PAD} Z`;
  const windowShare = data?.window_ai_share;

  return (
    <div className="rounded-md border border-border bg-card p-3" data-testid="ai-share-sparkline">
      <div className="mb-1 flex items-center justify-between text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Activity className="h-3 w-3" /> AI-share trend · {days}d
        </span>
        <span data-testid="ai-share-sparkline-window">
          {typeof windowShare === 'number' ? `${windowShare}% avg` : '—'}
        </span>
      </div>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}
           preserveAspectRatio="none"
           data-testid="ai-share-sparkline-svg"
           className="block">
        <defs>
          <linearGradient id="aiShareFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor="currentColor" stopOpacity="0.35" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0"    />
          </linearGradient>
        </defs>
        {/* 50% guide line */}
        <line x1={PAD} y1={y(50)} x2={W - PAD} y2={y(50)}
              stroke="currentColor" strokeOpacity="0.15" strokeDasharray="2 3" />
        <path d={areaPath} fill="url(#aiShareFill)"  className="text-primary" />
        <path d={linePath} fill="none" stroke="currentColor"
              strokeWidth="1.5" strokeLinejoin="round"
              className="text-primary" />
        {/* Tap targets on every active point so writers can drill in. */}
        {pts.map((p, i) => p.total > 0 && (
          <circle
            key={p.day}
            cx={x(i)} cy={y(p.share)} r="6"
            fill="transparent"
            style={{ cursor: 'pointer' }}
            data-testid={`ai-share-sparkline-tap-${p.day}`}
            onClick={() => setOpenDay(p.day)}
          >
            <title>{`${p.day} · ${p.total} action${p.total === 1 ? '' : 's'} · ${p.share}% AI`}</title>
          </circle>
        ))}
        {/* Last point marker */}
        <circle cx={x(n - 1)} cy={y(pts[n - 1].share)} r="2.5"
                className="fill-primary" />
      </svg>
      <div className="mt-1 flex items-baseline justify-between text-[10.5px] text-muted-foreground">
        <span>{pts[0]?.day}</span>
        <span data-testid="ai-share-sparkline-active-days">
          {data?.active_days ?? 0} active day{data?.active_days === 1 ? '' : 's'} · tap a point to drill in
        </span>
        <span>{pts[n - 1]?.day}</span>
      </div>
      <AiShareTrendDeepDiveDialog
        open={!!openDay}
        onOpenChange={(v) => (!v ? setOpenDay(null) : null)}
        day={openDay}
      />
    </div>
  );
}
